import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { API_ENDPOINTS } from "../../config/apiConfig.js";
import { useAuth } from "../../components/router/authContext.jsx";
import { buildAnalyticsCaseDetailPath } from "./analyticsViewMode.js";

const formatRelativeTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const FleetPlanningDashboard = () => {
  const [cardRef, isCardVisible] = useScrollAnimation({ threshold: 0.2, triggerOnce: true });
  const [latestCase, setLatestCase] = useState(null);
  const [latestIncompleteCase, setLatestIncompleteCase] = useState(null);
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.id || !authToken) {
      return;
    }

    let isMounted = true;
    const headers = { Authorization: `Token ${authToken}` };

    const loadContinuationState = async () => {
      try {
        const [incompleteCaseResponse, latestCaseResponse] = await Promise.allSettled([
          axios.get(API_ENDPOINTS.fleetLatestIncompleteCase, { headers }),
          axios.get(API_ENDPOINTS.getLatestOptimization, { headers }),
        ]);

        if (isMounted && incompleteCaseResponse.status === "fulfilled") {
          setLatestIncompleteCase(incompleteCaseResponse.value?.data || null);
        }
        if (isMounted && latestCaseResponse.status === "fulfilled") {
          setLatestCase(latestCaseResponse.value?.data || null);
        }
      } catch (error) {
        // Ignore dashboard continuation state errors.
      }
    };

    loadContinuationState();
    return () => {
      isMounted = false;
    };
  }, [currentUser?.id, authToken]);

  return (
    <div className="fixed inset-0 flex flex-col flex-1 items-center justify-center min-h-screen p-0 m-0 bg-white">
      <div
        ref={cardRef}
        className={`w-full max-w-3xl font-ui transition-all duration-1000 ease-out transform ${
          isCardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-semibold text-gray-900">
            Welcome to AmpTrans eFleetPlan
          </h1>
          <p className="mt-3 text-sm text-gray-400">
            Evaluate fleet electrification cost-effectiveness with structured, data-driven analysis.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() =>
              navigate("/fleet/inputs?new=1", {
                state: { mode: "new", promptCaseName: true },
              })
            }
            className="group flex w-full items-center gap-5 rounded-xl border-2 border-blue-600 bg-blue-600 px-6 py-5 text-left shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 hover:border-blue-700 hover:shadow-blue-700/25"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/20 text-xl text-white">
              +
            </span>
            <div className="min-w-0">
              <p className="text-base font-semibold text-white">Start New Case</p>
              <p className="text-sm text-blue-100">Begin a fresh 6-step setup</p>
            </div>
          </button>

          <button
            onClick={() => {
              if (latestIncompleteCase?.id) {
                navigate("/fleet/inputs", {
                  state: {
                    mode: "incomplete",
                    caseId: latestIncompleteCase.id,
                    sourceCaseId: latestIncompleteCase.source_case || null,
                    caseName: latestIncompleteCase.case_name,
                    inputSnapshot: latestIncompleteCase.input_data || null,
                  },
                });
                return;
              }
              navigate("/fleet/inputs");
            }}
            className="group flex w-full items-center gap-5 rounded-xl border border-gray-200 bg-white px-6 py-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-lg text-blue-600">
              &#9998;
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-base font-semibold text-gray-900">Continue Incomplete Case</p>
                {latestIncompleteCase?.updated_at && (
                  <span className="shrink-0 text-xs text-gray-300">{formatRelativeTime(latestIncompleteCase.updated_at)}</span>
                )}
              </div>
              <p className="text-sm text-gray-400">
                {latestIncompleteCase?.case_name ? `Continue "${latestIncompleteCase.case_name}"` : "No incomplete case yet"}
              </p>
            </div>
          </button>

          <button
            onClick={() => {
              if (latestCase?.id) {
                navigate(buildAnalyticsCaseDetailPath(latestCase.id));
                return;
              }
              navigate("/fleet/analytics");
            }}
            className="group flex w-full items-center gap-5 rounded-xl border border-gray-200 bg-white px-6 py-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-lg text-emerald-600">
              &#9656;
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-base font-semibold text-gray-900">Continue Latest Result</p>
                {latestCase?.timestamp && (
                  <span className="shrink-0 text-xs text-gray-300">{formatRelativeTime(latestCase.timestamp)}</span>
                )}
              </div>
              <p className="text-sm text-gray-400">
                {latestCase?.case_name ? `Open "${latestCase.case_name}"` : "No completed cases yet"}
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate("/fleet/analytics")}
            className="group flex w-full items-center gap-5 rounded-xl border border-gray-200 bg-white px-6 py-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-lg text-amber-600">
              &#9776;
            </span>
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900">Open Case Library</p>
              <p className="text-sm text-gray-400">Review, compare, and duplicate cases</p>
            </div>
          </button>
        </div>

        <p className="mt-8 text-center text-xs text-gray-300">
          Questions? Reach us at{' '}
          <a href="mailto:info@amptrans.net" className="text-gray-400 underline hover:text-blue-500">
            info@amptrans.net
          </a>
        </p>
      </div>
    </div>
  );
};

export default FleetPlanningDashboard;


